import { useState, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
function NumberField({ value, onChange, prefix }) {
  return (
    <Form.Control
      type="number"
      min="0"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={prefix ? `${prefix}0` : "0"}
      className="text-end"
    />
  );
}

function CalcRow({ label, amountInput, countInput, total, bold }) {
  return (
    <tr className={bold ? "fw-bold" : ""}>
      <td>{label}</td>
      <td>{amountInput}</td>
      <td>{countInput}</td>
      <td className="text-end">{total}</td>
    </tr>
  );
}

export default function PaymentsCalculatorModal({ show, onClose, THEME_COLOR }) {
  const themeAlpha = (opacity) => `${THEME_COLOR}${opacity}`;
  const [values, setValues] = useState({
    playersCount: 0,
    entranceAmount: 0,
    entranceCount: 0,
    ballAmount: 0,
    ballCount: 0,
    courtAmount: 0,
    courtCount: 0,
    miscAddAmount: 0,
    miscLessAmount: 0,
  });
  
  const [copied, setCopied] = useState(false);
  const {
    totalEntrance,
    totalBall,
    totalCourt,
    grandTotal,
    perHead,
    totalRent,
  } = useMemo(() => {
    const tEntrance = values.entranceAmount * values.entranceCount;
    const tBall = values.ballAmount * values.ballCount;
    const tCourt = values.courtAmount * values.courtCount;
    const gTotal =
      tEntrance + tBall + tCourt + values.miscAddAmount - values.miscLessAmount;
    const pHead =
      values.playersCount > 0 ? gTotal / values.playersCount : 0;

    return {
      totalEntrance: tEntrance,
      totalBall: tBall,
      totalCourt: tCourt,
      grandTotal: gTotal,
      perHead: pHead,
      totalRent: tEntrance + tCourt,
    };
  }, [values]);

  const handleChange = (key, val) => {
    setValues((prev) => ({
      ...prev,
      [key]: Number(val) || 0,
    }));
  };

  const handleReset = () => {
    setValues({
      playersCount: 0,
      entranceAmount: 0,
      entranceCount: 0,
      ballAmount: 0,
      ballCount: 0,
      courtAmount: 0,
      courtCount: 0,
      miscAddAmount: 0,
      miscLessAmount: 0,
    });
  };

  const money = (n) =>
    new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(n);


  // Copy Function
  const handleCopy = () => {
    const summary = `🥒🏓 *Game Breakdown*
---------------------------
👥 Players: ${values.playersCount}
💰 Overall Total: ${money(grandTotal)}
🔥 *Fee Per Player: ${money(perHead)}*
---------------------------
Court Rent: ${money(totalRent)}
Balls: ${money(totalBall)}`;

    navigator.clipboard.writeText(summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000); // Reset button text after 2s
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      scrollable
      contentClassName="border-0 shadow-lg rounded-5"
    >
      <Modal.Header closeButton className="border-0 px-4 pt-4">
        <Modal.Title className="fw-bold d-flex align-items-center" style={{ color: THEME_COLOR }}>
          <div 
            className="rounded-3 d-flex align-items-center justify-content-center me-3"
            style={{ width: 40, height: 40, backgroundColor: themeAlpha('10') }}
          >
            <i className="bi bi-calculator"></i>
          </div>
          Game Calculator
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="text-muted small fw-bold">Enter values to calculate split</div>
          <div className="d-flex gap-2">
            <Button 
              variant={copied ? "success" : "outline-primary"} 
              size="sm" 
              className="rounded-pill px-3 fw-bold"
              onClick={handleCopy}
            >
              {copied ? (
                <><i className="bi bi-check-lg me-1"></i>Copied!</>
              ) : (
                <><i className="bi bi-copy me-1"></i>Copy Summary</>
              )}
            </Button>
            <Button variant="outline-secondary" size="sm" className="rounded-pill px-3 fw-bold" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </div>

        <div style={{ maxHeight: "60vh", overflowY: "auto" }} className="custom-scrollbar px-1">
          <Table borderless hover responsive className="align-middle">
            <thead className="sticky-top" style={{ zIndex: 10 }}>
              <tr className="text-muted small fw-bold text-uppercase border-bottom" style={{ fontSize: '0.7rem' }}>
                <th className="ps-0">Item</th>
                <th className="text-center" style={{ width: '140px' }}>Amount</th>
                <th className="text-center" style={{ width: '100px' }}>Count</th>
                <th className="text-end pe-0">Total</th>
              </tr>
            </thead>

            <tbody>
              <CalcRow
                label="PLAYERS"
                countInput={
                  <NumberField
                    value={values.playersCount}
                    onChange={(v) => handleChange("playersCount", v)}
                  />
                }
              />

              <CalcRow
                label="ENTRANCE"
                total={money(totalEntrance)}
                amountInput={
                  <NumberField
                    prefix="₱"
                    value={values.entranceAmount}
                    onChange={(v) => handleChange("entranceAmount", v)}
                  />
                }
                countInput={
                  <NumberField
                    value={values.entranceCount}
                    onChange={(v) => handleChange("entranceCount", v)}
                  />
                }
              />

              <CalcRow
                label="BALLS"
                total={money(totalBall)}
                amountInput={
                  <NumberField
                    prefix="₱"
                    value={values.ballAmount}
                    onChange={(v) => handleChange("ballAmount", v)}
                  />
                }
                countInput={
                  <NumberField
                    value={values.ballCount}
                    onChange={(v) => handleChange("ballCount", v)}
                  />
                }
              />

              <CalcRow
                label="COURT"
                total={money(totalCourt)}
                amountInput={
                  <NumberField
                    prefix="₱"
                    value={values.courtAmount}
                    onChange={(v) => handleChange("courtAmount", v)}
                  />
                }
                countInput={
                  <NumberField
                    value={values.courtCount}
                    onChange={(v) => handleChange("courtCount", v)}
                  />
                }
              />

              <CalcRow
                label="MISC (ADD)"
                total={money(values.miscAddAmount)}
                amountInput={
                  <NumberField
                    prefix="₱"
                    value={values.miscAddAmount}
                    onChange={(v) => handleChange("miscAddAmount", v)}
                  />
                }
              />

              <CalcRow
                label="MISC (LESS)"
                total={`(${money(values.miscLessAmount)})`}
                amountInput={
                  <NumberField
                    prefix="₱"
                    value={values.miscLessAmount}
                    onChange={(v) => handleChange("miscLessAmount", v)}
                  />
                }
              />

              {/* Summary Section */}
              <tr className="border-top">
                 <td colSpan={4} className="py-2"></td>
              </tr>
              <CalcRow label="OVERALL TOTAL" total={money(grandTotal)} bold />
              <CalcRow label="FEE PER PLAYER" total={money(perHead)} bold color={THEME_COLOR} />
              <CalcRow label="TOTAL RENT" total={money(totalRent)} />
              <CalcRow label="TOTAL BALLS" total={money(totalBall)} />
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      
      <Modal.Footer className="border-0 pt-0 px-4 pb-4">
        <Button className="w-100 rounded-pill fw-bold py-2" onClick={onClose}>
          CLOSE CALCULATOR
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
